<template>
  <div
    v-if="!disabled"
    v-on-click-outside="closeMenu"
    class="search-as-you-type-filter"
    role="combobox"
    :aria-expanded="isMenuOpen"
    :aria-controls="'dropdown-' + filter"
    :aria-labelledby="`label-${filter}`"
    :aria-haspopup="true"
  >
    <FilterDropdownButton
      :id="`label-${filter}`"
      :is-open="isMenuOpen"
      :label="`${filter} filter dropdown`"
      :count="activeFilters.length"
      @on-click="toggleMenu()"
    >
      <slot>Label</slot>
    </FilterDropdownButton>

    <div v-if="isMenuOpen" class="search-as-you-type-filter__dropdown-menu">
      <SelectSearchBar
        v-model.trim="searchField"
        aria-label="Rechercher un auteur"
        @update:model-value="search"
        @keydown.escape="handleEscapeSearchBar"
      />

      <template v-if="!isLoading">
        <ul
          v-if="activeFilters.length > 0"
          class="search-as-you-type-filter__selected__list"
          role="group"
          aria-label="Filtres sélectionnés"
          @wheel.stop
        >
          <li
            v-for="(item, index) in activeFilters"
            :key="index"
            class="search-as-you-type-filter__selected__item"
            role="option"
          >
            <CheckBox
              checked
              @update:checked="(checked) => toggleFilter(item, checked)"
            >
              {{ getLabel(item) }}
            </CheckBox>
          </li>
        </ul>

        <SeparatorLine v-if="activeFilters.length > 0" />

        <ul
          role="listbox"
          :aria-label="`${filter} options disponibles`"
          class="search-as-you-type-filter__option__list"
        >
          <template v-for="(item, index) in items" :key="index">
            <li
              v-if="!activeFilters.includes(item)"
              role="option"
              class="search-as-you-type-filter__option__item"
              :aria-selected="activeFilters.includes(item)"
            >
              <CheckBox
                @update:checked="(checked) => toggleFilter(item, checked)"
              >
                {{ getLabel(item) }}
              </CheckBox>
            </li>
          </template>

          <li
            v-if="items.length === 0 && !isLoading"
            aria-live="polite"
            class="search-as-you-type-filter__no-results"
          >
            {{
              searchField && !isTyping
                ? "Aucun auteur correspondant à cette recherche"
                : "Écrivez quelques lettres pour commencer la recherche"
            }}
          </li>
        </ul>
      </template>

      <div v-else role="status" aria-live="polite">
        <p>Chargement...</p>
        <Minerve
          :mouvements="['blink', 'scan']"
          accessoire="loupe"
          :taille="64"
        />
      </div>

      <div class="search-as-you-type-filter__reset">
        <button
          v-focus
          class="btn-secondary search-as-you-type-filter__reset__button"
          aria-label="Réinitialiser le filtre"
          @click="reset"
          @keydown.tab="handleTabKey"
          @keydown.escape="closeMenu"
        >
          <i class="fa-solid fa-arrow-rotate-left"></i>
          <span class="search-as-you-type-filter__reset__text">
            Réinitialiser
          </span>
        </button>
      </div>
    </div>
  </div>

  <button
    v-else
    aria-disabled="true"
    class="search-as-you-type-filter__disabled"
  >
    <span>
      <slot />
    </span>
    <i class="fa-solid fa-chevron-down"></i>
  </button>
</template>

<script setup>
import { ref } from "vue";
import { useDebounceFn } from "@vueuse/core";
import { vOnClickOutside } from "@vueuse/components";
import Minerve from "@follaw-fr/components/reusable/minerve/Minerve";
import SelectSearchBar from "@follaw-fr/components/reusable/atoms/SelectSearchBar.vue";
import CheckBox from "@follaw-fr/components/reusable/atoms/CheckBox.vue";
import SeparatorLine from "@follaw-fr/components/reusable/atoms/SeparatorLine.vue";
import FilterDropdownButton from "../atoms/FilterDropdownButton.vue";
import { useToggleMenu } from "@follaw-fr/composables/useToggleMenu";

const props = defineProps({
  filter: {
    type: String,
    required: true,
  },
  items: {
    type: Array,
    required: true,
  },
  itemLabel: {
    type: String,
    default: null,
  },
  itemValue: {
    type: String,
    default: null,
  },
  activeFilters: {
    type: Array,
    default() {
      return [];
    },
  },
  isLoading: {
    type: Boolean,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "search",
  "resetFilter",
  "addFilterItem",
  "removeFilterItem",
]);

const { isMenuOpen, toggleMenu, closeMenu } = useToggleMenu();

const searchField = ref("");
const isTyping = ref(false);

const search = () => {
  isTyping.value = true;
  debounceSearch();
};
const debounceSearch = useDebounceFn(() => {
  isTyping.value = false;
  emit("search", searchField.value);
}, 500);

const getLabel = (item) => (props.itemLabel ? item[props.itemLabel] : item);

const reset = () => {
  searchField.value = "";
  emit("resetFilter", props.filter);
};

const toggleFilter = (item, isChecked) => {
  if (isChecked) {
    emit("addFilterItem", { filter: props.filter, item });
  } else {
    emit("removeFilterItem", { filter: props.filter, item });
  }
};
const handleTabKey = (event) => {
  if (!event.shiftKey) {
    event.preventDefault();
    closeMenu();
  }
};

const handleEscapeSearchBar = () => {
  if (!searchField.value.length) {
    closeMenu();
  }
};
</script>

<style lang="scss" scoped>
.search-as-you-type-filter {
  position: relative;
  -webkit-user-select: none;
  user-select: none;
}

.search-as-you-type-filter__dropdown-menu {
  position: absolute;
  z-index: 10;
  top: 33px;
  left: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-shadow: 0 1px 6px #0000005d;
  padding: 4px;
  background: white;
  border-radius: 13px;
  width: 240px;
}

.search-as-you-type-filter__reset {
  display: flex;
  justify-content: flex-end;
  padding: 0 12px 4px 0;
}

.search-as-you-type-filter__reset__button {
  font-size: 0.9rem;
}

.search-as-you-type-filter__reset__text {
  margin-left: 4px;
}

.search-as-you-type-filter__option__list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 343px;
  overflow-y: auto;
  overscroll-behavior: contain;
  overflow-x: hidden;
}

.search-as-you-type-filter__selected__list {
  @extend .search-as-you-type-filter__option__list;
  padding-top: 4px;
  max-height: 120px;
}

.search-as-you-type-filter__option__item,
.search-as-you-type-filter__selected__item {
  line-height: 1.5;
  text-transform: capitalize;
  padding: 0 11px;
  word-break: break-all;

  &:hover {
    background-color: var(--clr-p1);
    border-radius: 4px;
  }

  :deep(.checkbox-icon) {
    font-size: 1.063em;
  }

  :deep(.checkbox-input:hover + .checkbox-icon) {
    background-color: var(--clr-p1);
  }

  :deep(label) {
    width: 100%;
  }
}

.search-as-you-type-filter__no-results {
  padding: 4px 16px;
  color: var(--clr-n8);
  opacity: 0.6;
  font-style: italic;
}

.search-as-you-type-filter__disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
